import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';
import { platformDocs } from '../lib/helpContent';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircleQuestion, X, Send, Loader2, Bot, User } from 'lucide-react';

const PAGE_CONTEXT = {
  '/dashboard': 'Dashboard — KPIs, charts, recent ideas',
  '/ideas': 'Ideas list — browse, filter, bulk actions',
  '/ideas/new': 'Create New Idea — submit an improvement idea',
  '/projects': 'Projects list — browse, filter, bulk actions',
  '/reporting': 'Reporting — analytics dashboards',
  '/executive-summary': 'Executive Summary — leadership dashboard',
  '/processes': 'Process Hierarchy — tree view, maintenance',
  '/process-maps': 'Process Maps list — BPMN diagrams',
  '/process-maps/new': 'Create Process Map — BPMN editor',
  '/process-capture': 'Process Capture list — screen recordings',
  '/process-capture/new': 'New Process Capture — screen recorder',
  '/sop-generator': 'SOP Generator — standard operating procedures',
  '/audit-log': 'Audit Log — activity history',
  '/settings': 'Settings — configuration',
};

function getPageLabel(pathname) {
  // Exact match first
  if (PAGE_CONTEXT[pathname]) return PAGE_CONTEXT[pathname];
  // Prefix match
  for (const [path, label] of Object.entries(PAGE_CONTEXT)) {
    if (pathname.startsWith(path) && path !== '/') return label;
  }
  return 'Beyond Delta platform';
}

const HelpChatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const question = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setLoading(true);

    try {
      const pageContext = getPageLabel(location.pathname);
      const response = await api.post('/help/chat', {
        question,
        page_context: pageContext,
      });
      setMessages(prev => [...prev, { role: 'assistant', text: response.data.answer }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I couldn\'t process that. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#235894] text-white shadow-xl hover:bg-[#1E4B80] transition-all flex items-center justify-center hover:scale-105"
          data-testid="help-chatbot-btn"
        >
          <MessageCircleQuestion className="w-6 h-6" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border flex flex-col overflow-hidden" data-testid="help-chatbot-panel">
          {/* Header */}
          <div className="bg-[#235894] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              <div>
                <p className="font-semibold text-sm">Beyond Delta Help</p>
                <p className="text-xs text-white/70">{getPageLabel(location.pathname)}</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Bot className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                <p className="text-sm text-slate-500 mb-3">Ask me anything about Beyond Delta</p>
                <div className="space-y-2">
                  {['How do I link a process?', 'What are the 5 process levels?', 'How do I generate an SOP?'].map(q => (
                    <button key={q} onClick={() => { setInput(q); }} className="block w-full text-left text-xs px-3 py-2 rounded-lg border hover:bg-slate-50 text-slate-600">{q}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && <Bot className="w-5 h-5 text-[#235894] mt-1 flex-shrink-0" />}
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                  msg.role === 'user' ? 'bg-[#235894] text-white rounded-br-sm' : 'bg-slate-100 text-slate-700 rounded-bl-sm'
                }`}>
                  {msg.text}
                </div>
                {msg.role === 'user' && <User className="w-5 h-5 text-slate-400 mt-1 flex-shrink-0" />}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <Bot className="w-5 h-5 text-[#235894] mt-1" />
                <div className="bg-slate-100 rounded-xl px-3 py-2 rounded-bl-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t flex gap-2 flex-shrink-0">
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Ask a question..."
              className="text-sm"
              disabled={loading}
              data-testid="help-chat-input"
            />
            <Button onClick={handleSend} disabled={loading || !input.trim()} size="icon" className="bg-[#235894] hover:bg-[#1E4B80] flex-shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpChatbot;
